import { useRef } from 'react'
import type { FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { HrRecord, UserProfile } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, Input, Select, Skeleton, Textarea } from '../../components/ui'
import { DocIcon } from '../../components/icons'

interface DetailResponse { ok: boolean; target: UserProfile; record: HrRecord; can_manage: boolean }

export default function StaffDetail() {
  const { uid } = useParams<{ uid: string }>()
  const qc = useQueryClient()
  const contractRef = useRef<HTMLInputElement>(null)
  const medicalRef = useRef<HTMLInputElement>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'hr', 'staff', uid],
    queryFn: () => api.get<DetailResponse>(`/ops/hr/staff/${uid}`),
    enabled: Boolean(uid),
  })

  const save = useMutation({
    mutationFn: (form: FormData) => api.postForm(`/ops/hr/staff/${uid}`, form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'hr', 'staff', uid] })
      void qc.invalidateQueries({ queryKey: ['ops', 'hr', 'staff'] })
      if (contractRef.current) contractRef.current.value = ''
      if (medicalRef.current) medicalRef.current.value = ''
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    save.mutate(new FormData(e.currentTarget))
  }

  if (!uid) return <Navigate to="/hr" replace />
  if (isError) {
    return (
      <Alert tone="danger">
        {(error as Error).message}
        <div style={{ marginTop: 'var(--sp-3)' }}><Link to="/hr">Back to staff</Link></div>
      </Alert>
    )
  }
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={220} /></div>

  const { target, record, can_manage: canManage } = data

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{target.full_name || target.email || uid}</h1>
          <p className="page-head__sub">{titleCase(target.role)}{target.email ? ` · ${target.email}` : ''}</p>
        </div>
      </div>

      {save.isError && (
        <Alert tone="danger">{save.error instanceof ApiError ? save.error.message : 'Could not save that record.'}</Alert>
      )}

      <form onSubmit={onSubmit}>
        <fieldset disabled={!canManage} style={{ border: 'none', padding: 0, margin: 0 }}>
          <div className="grid grid--2" style={{ gap: 'var(--sp-4)' }}>
            <Card>
              <CardHeader title="Employment" />
              <CardBody>
                <div className="stack">
                  <Input label="Employee number" name="employee_number" defaultValue={record.employee_number} />
                  <Select label="Employment status" name="employment_status" defaultValue={record.employment_status || ''}>
                    <option value="">—</option>
                    <option value="active">Active</option>
                    <option value="on_leave">On leave</option>
                    <option value="suspended">Suspended</option>
                    <option value="terminated">Terminated</option>
                  </Select>
                  <Input label="Contract type" name="contract_type" defaultValue={record.contract_type} placeholder="e.g. Permanent, Fixed-term" />
                  <div className="grid grid--2">
                    <Input label="Contract start" name="contract_start" type="date" defaultValue={record.contract_start} />
                    <Input label="Contract end" name="contract_end" type="date" defaultValue={record.contract_end} />
                  </div>
                  <Input label="Signed contract (optional)" type="file" name="contract_file" ref={contractRef} accept="image/*,application/pdf" />
                  {record.contract_file_name && (
                    <a href={`/api/v1/ops/hr/staff/${uid}/document/contract`} target="_blank" rel="noreferrer"
                       style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
                      <DocIcon size={15} /> {record.contract_file_name}
                    </a>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Salary" />
              <CardBody>
                <div className="stack">
                  <div className="grid grid--2">
                    <Input label="Salary amount" name="salary_amount" type="number" step="any" min="0" defaultValue={record.salary_amount} />
                    <Input label="Currency" name="salary_currency" defaultValue={record.salary_currency || 'ZMW'} />
                  </div>
                  <Select label="Frequency" name="salary_frequency" defaultValue={record.salary_frequency || 'monthly'}>
                    <option value="monthly">Monthly</option>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="daily">Daily</option>
                    <option value="annual">Annual</option>
                  </Select>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Medical" />
              <CardBody>
                <div className="stack">
                  <Select label="Medical status" name="medical_status" defaultValue={record.medical_status || ''}>
                    <option value="">—</option>
                    <option value="fit">Fit</option>
                    <option value="fit_with_restrictions">Fit with restrictions</option>
                    <option value="unfit">Unfit</option>
                    <option value="pending">Pending exam</option>
                  </Select>
                  <div className="grid grid--2">
                    <Input label="Exam date" name="medical_exam_date" type="date" defaultValue={record.medical_exam_date} />
                    <Input label="Expiry date" name="medical_expiry_date" type="date" defaultValue={record.medical_expiry_date} />
                  </div>
                  <Input label="Medical record (optional)" type="file" name="medical_file" ref={medicalRef} accept="image/*,application/pdf" />
                  {record.medical_file_name && (
                    <a href={`/api/v1/ops/hr/staff/${uid}/document/medical`} target="_blank" rel="noreferrer"
                       style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-sm)' }}>
                      <DocIcon size={15} /> {record.medical_file_name}
                    </a>
                  )}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Emergency contact" />
              <CardBody>
                <div className="stack">
                  <Input label="Name" name="emergency_contact_name" defaultValue={record.emergency_contact_name} />
                  <Input label="Phone" name="emergency_contact_phone" defaultValue={record.emergency_contact_phone} />
                  <Textarea label="Notes" name="notes" defaultValue={record.notes} />
                </div>
              </CardBody>
            </Card>
          </div>

          {canManage && (
            <div className="row" style={{ marginTop: 'var(--sp-5)' }}>
              <Button type="submit" loading={save.isPending}>Save HR record</Button>
            </div>
          )}
        </fieldset>
      </form>

      {record.updated_at && (
        <p className="field__hint" style={{ marginTop: 'var(--sp-3)' }}>Last updated {formatDate(record.updated_at)}</p>
      )}
    </>
  )
}
